/**
 * EquipmentPalette — Draggable equipment palette for canvas editors.
 *
 * User drags an item onto the SVG canvas; the parent supplies an
 * onDropEquipment(type, svgX, svgY) callback that converts the drop
 * point into world coordinates and instantiates a new equipment.
 *
 * Props:
 *   items       — array of palette items (defaults to EQUIPMENT_PALETTE)
 *   onSelectAdd — clicking an item triggers "click-to-place" mode
 *                 (parent can use this for keyboard/click flows)
 *   draggingType — currently-dragging type, controlled by parent
 *   setDraggingType — setter for above (used to update on dragstart/end)
 */
import React from "react";
import {
   Cylinder, ArrowRightLeft, Layers3, Beaker, Database,
   Wind, FlaskConical, GitFork, Filter,
} from "lucide-react";
import { COL, mono, EQUIPMENT_PALETTE } from "./engineering_canvas_lib";

const ICONS = {
   vessel:         Cylinder,
   pump:           ArrowRightLeft,
   heat_exchanger: Layers3,
   column:         Beaker,
   tank:           Database,
   compressor:     Wind,
   reactor:        FlaskConical,
   valve:          GitFork,
   filter:         Filter,
};

export default function EquipmentPalette({
   items = EQUIPMENT_PALETTE,
   onSelectAdd,
   draggingType,
   setDraggingType,
}) {
   return (
      <div data-testid="equipment-palette"
            style={{ width: 110,
                        borderRight: `1px solid ${COL.border}`,
                        background: COL.panel, padding: "10px 6px",
                        overflow: "auto", ...mono, fontSize: 10,
                        color: COL.text }}>
         <div style={{ fontWeight: 700, fontSize: 10, letterSpacing: 0.5,
                            color: COL.dim, marginBottom: 8, paddingLeft: 4 }}>
            PALETTE
         </div>
         <div style={{ display: "grid",
                            gridTemplateColumns: "1fr",
                            gap: 6 }}>
            {items.map((it) => {
               const Icon = ICONS[it.type] || Cylinder;
               const isActive = draggingType === it.type;
               return (
                  <div key={it.type}
                        data-testid={`palette-item-${it.type}`}
                        draggable
                        onDragStart={(e) => {
                           e.dataTransfer.setData("text/eq-type", it.type);
                           e.dataTransfer.effectAllowed = "copy";
                           setDraggingType?.(it.type);
                        }}
                        onDragEnd={() => setDraggingType?.(null)}
                        onClick={() => onSelectAdd?.(it.type)}
                        title={`Drag onto canvas or click to add ${it.label}`}
                        style={{
                           display: "flex", flexDirection: "column",
                           alignItems: "center", gap: 4,
                           padding: "8px 4px",
                           background: isActive ? COL.active : COL.bg,
                           border: `1px solid ${isActive ? COL.spark
                                                               : COL.border}`,
                           borderRadius: 4,
                           cursor: "grab",
                           userSelect: "none",
                        }}>
                     <Icon size={20}
                            color={isActive ? "white" : COL.spark}/>
                     <span style={{ fontSize: 9,
                                         color: isActive ? "white"
                                                                : COL.text,
                                         textAlign: "center" }}>
                        {it.label}
                     </span>
                  </div>
               );
            })}
         </div>
         <div style={{ marginTop: 10, padding: "6px 4px",
                            fontSize: 9, color: COL.dim,
                            lineHeight: 1.4 }}>
            Drag onto canvas to add equipment.
         </div>
      </div>
   );
}
